import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn, secondaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";
import { Plus, X } from "lucide-react";

const GRADES = ["A", "B", "C", "D", "F"];

export default function GpaForecast() {
  const { user } = useAuth();
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [base, setBase] = useState({ current_gpa: 3.5, credits_completed: 30, intended_major: "Computer Science", target_school: "UF" });
  const [rows, setRows] = useState([
    { course_name: "Calculus II", credits: 4, projected_grade: "A" },
    { course_name: "Physics I", credits: 4, projected_grade: "B" },
    { course_name: "ENC 1102", credits: 3, projected_grade: "A" },
  ]);
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  const setB = (k) => (e) => setBase({ ...base, [k]: e.target.value });
  const setRow = (i, k) => (e) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: k === "credits" ? parseInt(e.target.value, 10) || 0 : e.target.value } : r));
  const addRow = () => setRows([...rows, { course_name: "", credits: 3, projected_grade: "B" }]);
  const rmRow = (i) => setRows(rows.filter((_, j) => j !== i));

  const next = (e) => { e.preventDefault(); setStage(user ? "running" : "gate"); if (user) run(); };
  const run = async (e) => {
    if (e) e.preventDefault();
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setLoading(true);
    try {
      const payload = {
        current_gpa: parseFloat(base.current_gpa), credits_completed: parseInt(base.credits_completed, 10) || 0,
        intended_major: base.intended_major, target_school: base.target_school,
        remaining_courses: rows.filter((r) => r.course_name.trim()),
        email: email || undefined, name: name || undefined,
      };
      const res = await api.post("/tools/gpa-forecast", payload);
      setResult(res.data); setStage("result");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Tool run failed.";
      toast.error(msg);
      if (err?.response?.status === 429) setStage("form");
    } finally { setLoading(false); }
  };

  return (
    <ToolPage
      numeral="II"
      name="GPA Forecast"
      blurb="Projects your final cumulative GPA against the competitive band for your target university and major."
      seoTitle="Free GPA Forecast Tool for Florida Transfer Students | Beyond Valencia"
      seoDescription="Project your final cumulative GPA against the competitive admissions band at UF, UCF, FSU, and other transfer targets. Add remaining courses and projected grades."
      seoCanonical="/tools/gpa-forecast"
      seoKeywords="GPA forecast, transfer GPA calculator, UF GPA requirement, Florida transfer GPA"
    >
      {stage === "form" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-4xl">
          <SectionLabel index="A" label="Current state" />
          <form onSubmit={next} className="mt-7 space-y-5" data-testid="gpa-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Current cumulative GPA</Label><Input data-testid="gpa-current" className={inputCls} value={base.current_gpa} onChange={setB("current_gpa")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Credits completed</Label><Input data-testid="gpa-credits" type="number" className={inputCls} value={base.credits_completed} onChange={setB("credits_completed")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Intended major</Label><Input className={inputCls} value={base.intended_major} onChange={setB("intended_major")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Target school</Label><Input className={inputCls} value={base.target_school} onChange={setB("target_school")} required /></div>
            </div>

            <div className="pt-3">
              <SectionLabel index="B" label="Remaining courses" />
              <div className="mt-5 border-t border-l border-stone/40">
                {rows.map((r, i) => (
                  <div key={i} className="border-r border-b border-stone/40 grid grid-cols-12 items-center gap-2 p-3 bg-bone">
                    <div className="col-span-7"><Input data-testid={`gpa-course-${i}-name`} className={inputCls} value={r.course_name} onChange={setRow(i, "course_name")} placeholder="Course name" /></div>
                    <div className="col-span-2"><Input type="number" className={inputCls} value={r.credits} onChange={setRow(i, "credits")} placeholder="cr" /></div>
                    <div className="col-span-2"><select className={`${inputCls} w-full h-9 px-3 text-sm`} value={r.projected_grade} onChange={setRow(i, "projected_grade")}>{GRADES.map((g) => (<option key={g} value={g}>{g}</option>))}</select></div>
                    <button type="button" onClick={() => rmRow(i)} className="col-span-1 flex justify-end text-stone hover:text-ink"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addRow} className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass" data-testid="gpa-add-row"><Plus className="h-3 w-3" /> Add course</button>
            </div>
            <button type="submit" className={primaryBtn} data-testid="gpa-next-btn">Continue</button>
          </form>
        </div>
      )}

      {stage === "gate" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-2xl" data-testid="gpa-gate">
          <SectionLabel index="C" label="One step before we run it" />
          <form onSubmit={run} className="mt-7 space-y-5">
            <div className="space-y-2"><Label className={labelCls}>First name</Label><Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-2"><Label className={labelCls}>Email</Label><Input data-testid="gpa-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="flex gap-3"><button type="submit" disabled={loading} className={primaryBtn} data-testid="gpa-submit-btn">{loading ? "Calculating…" : "Run my forecast"}</button><button type="button" onClick={() => setStage("form")} className={secondaryBtn}>Back</button></div>
          </form>
        </div>
      )}

      {(stage === "running" || loading) && stage !== "result" && (<div className="text-ink-soft">Projecting your GPA…</div>)}

      {stage === "result" && result && (
        <div className="space-y-0 border-t border-l border-stone/40 max-w-3xl" data-testid="gpa-result">
          {[
            { l: "Current GPA", v: result.current_gpa?.toFixed?.(2) ?? result.current_gpa, bg: "bg-bone" },
            { l: "Projected final GPA", v: result.projected_final_gpa?.toFixed?.(2) ?? result.projected_final_gpa, bg: "bg-bone-dim", highlight: true },
            { l: "Minimum required for target", v: result.minimum_required_for_target?.toFixed?.(2) ?? result.minimum_required_for_target, bg: "bg-bone" },
            { l: "Competitive for target", v: result.competitive_for_target?.toFixed?.(2) ?? result.competitive_for_target, bg: "bg-bone" },
          ].map((row, i) => (
            <div key={i} className={`border-r border-b border-stone/40 ${row.bg} p-5 flex items-baseline justify-between`}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone">{row.l}</div>
              <div className={`font-serif font-medium ${row.highlight ? "text-brass text-4xl" : "text-ink text-2xl"} tracking-tight tabular-nums`} data-testid={row.highlight ? "gpa-projected-final" : undefined}>{row.v}</div>
            </div>
          ))}
          <div className="border-r border-b border-stone/40 bg-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Competitive band — explanation</div>
            <p className="mt-3 text-ink-soft text-sm leading-relaxed">{result.competitive_for_target_explanation}</p>
          </div>
          <div className="border-r border-b border-stone/40 bg-ivy text-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Risk if grades decline</div>
            <p className="mt-3 text-bone-dim text-sm leading-relaxed">{result.risk_if_grades_decline}</p>
          </div>
          <div className="border-r border-b border-stone/40 bg-bone p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Recommended floor</div>
            <p className="mt-3 text-ink text-sm leading-relaxed">{result.recommended_floor}</p>
          </div>
          <p className="text-[11px] text-stone mt-6 italic">{result.disclaimer}</p>
        </div>
      )}
    </ToolPage>
  );
}
